selfIntroduction:
  en: |
    <p>Hi there! I study <em>intelligent collective behavior design for swarm agents</em>,
    using techniques from a variety of fields such as planning, search, learning,
    optimization, robotics, multi-agent systems, and distributed computing.
    Currently, I am affiliated with <a href="https://www.aist.go.jp/index_en.html">National Institute of Advanced Industrial Science and Technology (AIST), Japan</a> as a researcher.
    I received my Ph.D. from <a href="https://www.titech.ac.jp/">Tokyo Institute of Technology</a> in 2023, under the supervision of Prof. <a href="https://www.coord.c.titech.ac.jp/m/defago/">Xavier Défago</a> and then did a postdoc scholar at <a href="https://www.cam.ac.uk/">the University of Cambridge, UK</a>, working with Prof. <a href="https://www.proroklab.org">Amanda Prorok</a>.
    <p>The research outcomes have been pushed the boundaries of scalable multi-agent multi-robot systems. They have been used to win <a href="https://www.leagueofrobotrunners.org/">the international logistics automation competition sponsored by Amazon Robotics</a> (several times), and have been actually deployed in several warehouse companies, both without my involvement.
    In other words, my algorithms are controlling countless robots right now.
    Research collaborations are welcome!</p>
  ja: |
    <p>移動エージェント群を大規模に制御することに興味があり,
    プランニング, 探索, 最適化, 学習, ロボティクス, マルチエージェントシステム, 分散コンピューティングなどの知見を取り入れて研究を進めています.
    現在, <a href="https://www.aist.go.jp/">産業技術総合研究所 (AIST)</a>・<a href="https://www.airc.aist.go.jp/">人工知能研究センター (AIRC)</a> の研究員です.
    2023年3月に博士(工学)を<a href="https://www.titech.ac.jp/">東京工業大学</a> 情報理工学院より取得し (指導教員: <a href="https://www.coord.c.titech.ac.jp/m/defago/">Défago Xavier</a> 教授),
    その後, <a href="https://www.cam.ac.uk/">ケンブリッジ大学</a>でポスドク研究員をしていました (ホスト: <a href="https://proroklab.org/">Prorok Amanda</a> 教授, JSPS 海外学振).
    研究成果はスケーラブルなマルチエージェント・マルチロボットシステムの限界を押し広げるもので, <a href="https://www.leagueofrobotrunners.org/">Amazon Roboticsスポンサーの物流自動化コンペティション</a>では優勝手法に使用され, いくつかの物流倉庫では実際に導入されています.
    </p>
    <p>共同研究の提案や<a href="https://kei18.github.io/recruit">リサーチインターン</a>は歓迎です. 企業との共同研究・技術コンサルティングも展開しています. 気軽に連絡ください.</p>
heroTagline:
  en: Multi-Agent & Multi-Robot Researcher, PhD
  ja: Multi-Agent & Multi-Robot Researcher, PhD

cv: resume

hero:
  video: led-8drones-crf18
  mobile:
    - 40robots_1
    - 8drones_led

links:
  - title:
      ja: 募集
      en: Recruitment (JP)
    url: https://kei18.github.io/recruit/
  - title:
      en: AIST AIRC Team
      ja: AIST AIRC チーム
    url:
      ja: https://www.airc.aist.go.jp/cosine/
      en: https://www.airc.aist.go.jp/en/cosine/

photo: profile18_compressed

bio: |
  Keisuke Okumura is a Researcher at the National Institute of Advanced Industrial Science and Technology (AIST), Japan.
  He received his PhD in Computer Science from the Tokyo Institute of Technology in 2023, and subsequently worked as a Visiting Postdoctoral Scholar at the University of Cambridge until the end of 2025.
  His research focuses on developing scalable AI methods for collective automation, particularly at the intersection of multi-agent planning, heuristic search, learning, and multi-robot coordination.
  His honours include the ICAPS 2022 Best Student Paper Award, Tokyo Tech’s Ph.D. Dissertation Award (2024), and Funai Information Technology Award for Young Researchers (2026).
